import { shouldShowLabelNotPrintedWarning } from "@client-shared/lib/labelPrinting";
import { Badge } from "@bcc/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@bcc/components/ui/tooltip";
import { cn } from "@bcc/lib/utils";
import { DataTableEmptyCell } from "@bcc/components/data-table";
import type { PurchaseOrder } from "@shared/types";
import { CellContext } from "@tanstack/react-table";
import { Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ORDER_STATUS_CONFIG } from "../constants";

const LabelNotPrintedBadge = () => {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
          <Badge
            variant="secondary"
            className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400 cursor-default"
            data-testid="label-not-printed-badge"
          >
            <Printer className="h-3 w-3 mr-1" />
            {t("views.orders.labelNotPrinted.badge")}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{t("views.orders.labelNotPrinted.tooltip")}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export const OrderStatusCell = (props: CellContext<PurchaseOrder, unknown>) => {
  const { t } = useTranslation();
  const { orderStatus: status, beanzConnectLabelPrintEnabled, lineItems } = props.row.original;

  if (!status) return <DataTableEmptyCell />;

  const config = (ORDER_STATUS_CONFIG as Partial<typeof ORDER_STATUS_CONFIG>)[status];
  if (!config) return <DataTableEmptyCell />;
  const showLabelNotPrinted = shouldShowLabelNotPrintedWarning({
    beanzConnectLabelPrintEnabled,
    orderStatus: status,
    lineItems,
  });

  return (
    <div className="flex items-center gap-1">
      <Badge variant="secondary" className={cn("text-xs", config.className)}>
        {t(config.labelKey) as string}
      </Badge>
      {showLabelNotPrinted && <LabelNotPrintedBadge />}
    </div>
  );
};
